import 'react-daterange-picker/dist/css/react-calendar.css';

import DateRangePicker from 'react-daterange-picker';
import styled from 'styled-components';

import ChevronUrl from '../../assets/chevron.svg';
import { ThemeProp } from '../../theme/Themes';
import DateTimeInput from '../DateTimeInput';

const breakpoints = {
  oneColumn: '38.375rem',
  hiddenRightPanel: '53.375rem',
};

export const Container = styled.div`
  display: flex;
`;

export const RangeSelectionContainer = styled.div`
  display: flex;
  padding: 0 1rem;
  flex-direction: column;
  width: 100%;
  min-height: 22.25rem;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding: 0;
  }
`;

export const PredefinedSelectionsColumnContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 0 10.5rem;
  border-right: 1px solid ${({ theme }) => theme.colors.neutrals.c700};
  margin-right: 1rem;
  padding-right: 1rem;
  @media (max-width: ${breakpoints.hiddenRightPanel}) {
    display: none;
  }
`;

export const PredefinedSelectionsRowContainer = styled.div`
  display: none;
  margin: 0 0.5rem;
  gap: 0.5rem;
  padding-bottom: 1rem;
  @media (max-width: ${breakpoints.hiddenRightPanel}) {
    display: flex;
  }
`;

export const InputsContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  margin: 0 0.5rem;
  @media (max-width: ${breakpoints.oneColumn}) {
    flex-direction: column;
    gap: 0;
  }
`;

interface DateRangePickerProps {
  skipPendingState?: boolean;
}

export const StyledDateRangePicker = styled(DateRangePicker)<DateRangePickerProps>`
  .DateRangePicker__Month {
    width: calc(50% - 1rem);
    margin: 0 0.5rem;
    @media (max-width: ${breakpoints.oneColumn}) {
      width: calc(100% - 1rem);
      &:nth-of-type(3) {
        margin-top: 1rem;
      }
    }
  }
  .DateRangePicker__MonthHeader {
    color: ${({ theme }) => theme.colors.neutrals.c300};
    font-size: ${({ theme }) => theme.fontVariants.desktop.buttons.sm.fontSize};
    align-items: center;
    display: flex;
    justify-content: center;
    gap: 0.3rem;
  }

  .null__PaginationArrow {
    border: 0;
    cursor: pointer;
    display: block;
    height: 35px;
    outline: none;
    overflow: hidden;
    padding: 0;
    position: absolute;
    text-align: center;
    top: 0;
    white-space: nowrap;
    width: 35px;
    z-index: 1;
  }
  .null__PaginationArrow:hover {
    background-color: transparent;
  }

  .null__PaginationArrow--previous {
    left: 0.5rem;
  }

  .null__PaginationArrow--next {
    right: 0.5rem;
  }
  .null__PaginationArrowIcon {
    width: 1.5rem;
    background-image: url(${ChevronUrl});
    border-bottom: 8px solid transparent;
    border-top: 8px solid transparent;
    height: 0;
    position: absolute;
    top: 10px;
    width: 0;
  }
  .null__PaginationArrowIcon--previous {
    transform: rotate(0deg);
    border-right: 8px solid #aaa;
    left: 10px;
  }
  .null__PaginationArrowIcon--next {
    border-left: unset;
    transform: rotate(180deg);
    border-right: 8px solid #aaa;
    right: 10px;
  }
  .null__PaginationArrowIcon--is-disabled {
    opacity: 0.25;
  }

  .DateRangePicker__Date--otherMonth {
    opacity: 1;
    .DateRangePicker__DateLabel {
      color: ${({ theme }) => theme.colors.neutrals.c300};
    }
    &.DateRangePicker__Date--is-selected {
      background-color: ${({ theme }) => theme.colors.primary.c800};
      .DateRangePicker__DateLabel {
        color: ${({ theme }) => theme.colors.neutrals.white};
      }
    }
  }
  .DateRangePicker__Date {
    border: none;
    color: ${({ theme }) => theme.colors.neutrals.c300};
  }
  .DateRangePicker__WeekdayHeading {
    border-bottom-width: 0;
    & > abbr[title] {
      font-weight: ${({ theme }) => theme.fontWeight.regular};
      font-size: ${({ theme }) => theme.fontVariants.desktop.caption.md.fontSize};
      color: ${({ theme }) => theme.colors.neutrals.c300};
    }
  }
  .DateRangePicker__Date--is-disabled {
    .DateRangePicker__DateLabel {
      color: ${({ theme }) => theme.colors.neutrals.c500};
    }
  }
  .DateRangePicker__Date--weekend {
    background-color: transparent;
  }
  .DateRangePicker__MonthDates {
    border-bottom: unset;
    border-spacing: 0;
  }
  // Styles for the selected date range
  .DateRangePicker__CalendarSelection {
    background-color: #145d524d;
    bottom: 0;
    top: 0;
    left: 0;
    right: 0;
    border-radius: 0;
    border: unset;
    .DateRangePicker__Date--is-selected {
      background-color: ${({ theme }) => theme.colors.primary.c800};
      color: ${({ theme }) => theme.colors.neutrals.white};
    }
  }
  .DateRangePicker__Date--is-selected {
    background-color: ${({ theme }) => theme.colors.primary.c800};
    .DateRangePicker__DateLabel {
      color: ${({ theme }) => theme.colors.neutrals.white};
    }
  }

  // Styles for hovering over a date
  .DateRangePicker__CalendarHighlight--single {
    background-color: ${({ theme }) => theme.colors.neutrals.c700};
    color: ${({ theme }) => theme.colors.neutrals.c300};
    border: none;
    border-radius: 0;
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
  }

  // Styles for start and end date
  .DateRangePicker__Date {
    &.DateRangePicker__Date {
      .DateRangePicker__CalendarSelection--start {
        background-color: ${({ theme }) => theme.colors.primary.c800};
      }

      .DateRangePicker__CalendarSelection--end {
        background-color: ${({ theme }) => theme.colors.primary.c800};
      }
    }
  }

  .DateRangePicker__CalendarSelection--single--is-pending {
    color: ${({ theme }) => theme.colors.neutrals.black};
    ${(props: DateRangePickerProps) => props.skipPendingState && 'background-color: transparent;'}

    @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
      background-color: ${({ theme }) => theme.colors.primary.c800};
    }
  }
`;

export const StyledDateTimeInput = styled(DateTimeInput)`
  & .nextui-input {
    color: ${({ theme }) => theme.colors.neutrals.white};
  }
`;

interface PredefinedSelectionButtonProps {
  isFirst?: boolean;
  isSelected?: boolean;
}

type ButtonProps = PredefinedSelectionButtonProps & ThemeProp;

export const PredefinedSelectionButton = styled.div<PredefinedSelectionButtonProps>`
  display: flex;
  align-items: center;
  height: 2.875rem;
  padding: 0 1rem;
  cursor: pointer;
  font-size: ${({ theme }) => theme.fontVariants.desktop.body.sm.fontSize};
  line-height: ${({ theme }) => theme.fontVariants.desktop.body.sm.lineHeight};
  background-color: ${(props: ButtonProps) => (props.isSelected ? props.theme.colors.neutrals.c700 : 'transparent')};
  color: ${({ theme }) => theme.colors.neutrals.c300};

  @media (max-width: ${breakpoints.hiddenRightPanel}) {
    height: 2.375rem;
    text-align: center;
    justify-content: center;
  }
  @media (max-width: ${breakpoints.oneColumn}) {
    padding: 0 0.5rem;
    font-size: ${({ theme }) => theme.fontSize.s};
    flex-grow: 1;
  }
`;

export const CustomPredefinedRangeButton = styled(PredefinedSelectionButton)`
  @media (max-width: ${breakpoints.oneColumn}) {
    display: none;
  }
`;

export const MessageContainer = styled.div`
  display: flex;
  align-items: center;
  max-width: 29.5rem;
  padding: 0.75rem;
  margin: 0 0.5rem 1rem 0.5rem;
  gap: 0.75rem;
  font-size: ${({ theme }) => theme.fontVariants.desktop.caption.md.fontSize};
  line-height: ${({ theme }) => theme.fontVariants.desktop.caption.md.lineHeight};
`;

export const MinimumDateErrorMessageContainer = styled(MessageContainer)`
  background: ${(props: ThemeProp) => `${props.theme.colors.system.error.c300}1A`};
  color: ${({ theme }) => theme.colors.system.error.c100};

  svg {
    path {
      fill: ${({ theme }) => theme.colors.system.error.c300};
    }
  }
`;

export const IconContainer = styled.div`
  display: flex;
  width: 1.125rem;
`;

export const MinimumDateInformationMessageContainer = styled(MessageContainer)`
  margin-top: 0.75rem;
  background: ${(props: ThemeProp) => `${props.theme.colors.primary.c500}14`};
  color: ${({ theme }) => theme.colors.primary.c100};

  svg {
    path {
      fill: ${({ theme }) => theme.colors.primary.c300};
    }
  }
`;
